/**
 * AdminAIPlayground.jsx
 *
 * AI Playground — the end-to-end console for the full AI pipeline:
 *
 *   Prompt → Provider (Mock today; OpenAI/Claude/Gemini/Local listed but
 *   disabled) → Creative Director → Template Factory → Design Engine
 *
 * Fully offline: no network calls, no SDKs, no API keys. When a real
 * provider is implemented later, it appears in the provider dropdown
 * automatically (isReady() flips) and this page needs zero changes.
 */
import React, { useMemo, useRef, useState } from 'react';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Bot, Play, Square, Copy, CheckCircle2, XCircle, Gauge, Braces,
  TerminalSquare, Clapperboard, FileJson, Sparkles,
} from 'lucide-react';
import {
  generateFromPrompt,
  listProviders,
  getProvider,
  CancellationToken,
  STAGES,
  STAGE_ORDER,
} from '../../aiOrchestrator';
import { listAudiences, listMoods, listBrandStyles } from '../../aiCreativeDirector';
import { listCategories } from '../../aiTemplateFactory';

const STAGE_LABELS = {
  [STAGES.QUEUED]: 'Queued',
  [STAGES.THINKING]: 'Thinking',
  [STAGES.CREATIVE_DIRECTION]: 'Creative Direction',
  [STAGES.TEMPLATE_FACTORY]: 'Template Factory',
  [STAGES.DESIGN_ENGINE]: 'Design Engine',
  [STAGES.COMPLETED]: 'Completed',
};

const TABS = [
  { id: 'decisions', label: 'Creative Decisions', icon: Clapperboard },
  { id: 'designSpec', label: 'DesignSpec', icon: FileJson },
  { id: 'commands', label: 'Commands', icon: TerminalSquare },
  { id: 'quality', label: 'Quality Score', icon: Gauge },
  { id: 'json', label: 'Full JSON', icon: Braces },
];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-medium text-gray-600 mb-1 block">{label}</span>
    {children}
  </label>
);

const SelectBox = ({ value, onChange, options, disabledIds = [] }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
  >
    {options.map((o) => (
      <option key={o.value} value={o.value} disabled={disabledIds.includes(o.value)}>
        {o.label}
      </option>
    ))}
  </select>
);

const ScoreBar = ({ label, value }) => (
  <div className="flex items-center gap-2">
    <span className="w-28 text-xs text-gray-500 capitalize flex-shrink-0">{label}</span>
    <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
      <div
        className={`h-full rounded-full ${value >= 75 ? 'bg-emerald-500' : value >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
        style={{ width: `${value}%` }}
      />
    </div>
    <span className="w-8 text-right text-xs font-semibold text-gray-700">{value}</span>
  </div>
);

const JsonBlock = ({ data }) => (
  <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs overflow-auto max-h-96 leading-relaxed">
    {JSON.stringify(data, null, 2)}
  </pre>
);

const AdminAIPlayground = () => {
  const [prompt, setPrompt] = useState('Grand opening flyer for a burger restaurant, warm and bold');
  const [providerId, setProviderId] = useState('mock');
  const [audience, setAudience] = useState('');
  const [mood, setMood] = useState('');
  const [brandStyle, setBrandStyle] = useState('');
  const [count, setCount] = useState(3);

  const [running, setRunning] = useState(false);
  const [stage, setStage] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('decisions');
  const [templateIdx, setTemplateIdx] = useState(0);
  const [copied, setCopied] = useState(false);
  const tokenRef = useRef(null);

  const providers = useMemo(listProviders, []);
  const audiences = useMemo(listAudiences, []);
  const moods = useMemo(listMoods, []);
  const styles = useMemo(listBrandStyles, []);
  const categories = useMemo(listCategories, []);

  const run = async () => {
    setError('');
    setResult(null);
    setRunning(true);
    setTemplateIdx(0);
    setCopied(false);
    tokenRef.current = new CancellationToken();
    try {
      const res = await generateFromPrompt({
        prompt,
        provider: getProvider(providerId),
        count: Math.max(1, Math.min(10, Number(count) || 1)),
        pins: {
          ...(audience ? { audience } : {}),
          ...(mood ? { mood } : {}),
          ...(brandStyle ? { brandStyle } : {}),
        },
        token: tokenRef.current,
        onProgress: (e) => setStage(e.stage),
      });
      setResult(res);
    } catch (e) {
      if (e.name !== 'CancelledError') setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  const cancel = () => tokenRef.current?.cancel('cancelled from playground');

  const copyAll = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(result, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable — ignore */ }
  };

  const template = result?.templates?.[templateIdx];
  const stageIndex = stage ? STAGE_ORDER.indexOf(stage) : -1;

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Bot className="w-6 h-6 text-purple-600" /> AI Playground
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Prompt-to-template pipeline, fully offline on the Mock provider.
              Paste an API key later by implementing one adapter — this page won’t change.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {running && (
              <Button variant="outline" onClick={cancel}>
                <Square className="w-4 h-4 mr-2" /> Cancel
              </Button>
            )}
            <Button onClick={run} disabled={running} className="bg-purple-600 hover:bg-purple-700">
              <Play className="w-4 h-4 mr-2" /> {running ? 'Generating…' : 'Generate'}
            </Button>
          </div>
        </div>

        {/* Prompt + pins */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 space-y-4">
          <Field label="Prompt">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              placeholder="Describe the flyer you want…"
            />
          </Field>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Field label="Provider">
              <SelectBox
                value={providerId}
                onChange={setProviderId}
                options={providers.map((p) => ({ value: p.id, label: p.name }))}
                disabledIds={providers.filter((p) => !p.ready).map((p) => p.id)}
              />
            </Field>
            <Field label="Audience (pin)">
              <SelectBox value={audience} onChange={setAudience}
                options={[{ value: '', label: 'Let AI decide' },
                  ...audiences.map((a) => ({ value: a.id, label: a.name }))]} />
            </Field>
            <Field label="Style (pin)">
              <SelectBox value={brandStyle} onChange={setBrandStyle}
                options={[{ value: '', label: 'Let AI decide' },
                  ...styles.map((s) => ({ value: s.id, label: s.name }))]} />
            </Field>
            <Field label="Mood (pin)">
              <SelectBox value={mood} onChange={setMood}
                options={[{ value: '', label: 'Let AI decide' },
                  ...moods.map((m) => ({ value: m.id, label: m.name }))]} />
            </Field>
            <Field label="Template count">
              <Input type="number" min={1} max={10} value={count}
                onChange={(e) => setCount(e.target.value)} className="h-9 text-sm" />
            </Field>
          </div>
        </div>

        {/* Pipeline stages */}
        {(running || result) && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
            <div className="flex items-center gap-1">
              {STAGE_ORDER.map((s, i) => (
                <React.Fragment key={s}>
                  {i > 0 && <div className={`flex-1 h-0.5 ${i <= stageIndex ? 'bg-purple-400' : 'bg-gray-200'}`} />}
                  <span className={`text-[11px] px-2 py-1 rounded-full whitespace-nowrap ${
                    i < stageIndex ? 'bg-purple-100 text-purple-700'
                      : i === stageIndex ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-400'
                  }`}>
                    {STAGE_LABELS[s]}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-2 text-sm text-red-700 mb-6">
            <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {error}
          </div>
        )}

        {!result && !running && !error && (
          <div className="bg-white rounded-xl border border-dashed border-gray-300 p-16 text-center text-gray-400">
            <Bot className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Write a prompt and hit <b>Generate</b>.</p>
            <p className="text-xs mt-1">
              The Mock provider reads your prompt offline — categories like {categories.slice(0, 4).map((c) => c.name.toLowerCase()).join(', ')}… are detected from keywords.
            </p>
          </div>
        )}

        {result && (
          <>
            {/* Brief summary */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-wrap items-center gap-2 text-sm">
              <Sparkles className="w-4 h-4 text-purple-500" />
              <span className="text-gray-700">
                Brief: <b>{result.brief.audience}</b>
                {result.brief.mood && <> · mood <b>{result.brief.mood}</b></>}
                {result.brief.brandStyle && <> · style <b>{result.brief.brandStyle}</b></>}
              </span>
              <span className="text-gray-400">·</span>
              <span className="text-gray-500">{result.templates.length} templates · {result.stats.attempts} attempts</span>
              {result.dropped.length > 0 && (
                <span className="text-amber-600 text-xs">
                  ({result.dropped.length} AI choice(s) sanitized)
                </span>
              )}
              <Button variant="outline" size="sm" className="ml-auto" onClick={copyAll}>
                {copied ? <CheckCircle2 className="w-4 h-4 mr-1 text-emerald-600" /> : <Copy className="w-4 h-4 mr-1" />}
                {copied ? 'Copied' : 'Copy full JSON'}
              </Button>
            </div>

            {/* Template selector + preview tabs */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {result.templates.map((t, i) => (
                  <button
                    key={t.fingerprint}
                    onClick={() => setTemplateIdx(i)}
                    className={`px-3 py-1.5 rounded-lg text-sm border ${
                      i === templateIdx
                        ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    #{i + 1} · {t.qualityScore}
                  </button>
                ))}
                <div className="ml-auto flex items-center gap-1">
                  {TABS.map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => setActiveTab(id)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs ${
                        activeTab === id ? 'bg-purple-600 text-white' : 'text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" /> {label}
                    </button>
                  ))}
                </div>
              </div>

              {activeTab === 'decisions' && <JsonBlock data={result.direction} />}
              {activeTab === 'designSpec' && template && <JsonBlock data={template.designSpec} />}
              {activeTab === 'commands' && template && (
                <>
                  <p className="text-xs text-gray-400 mb-2">{template.commands.length} editor commands — structured, never markup.</p>
                  <JsonBlock data={template.commands} />
                </>
              )}
              {activeTab === 'quality' && template && (
                <div className="max-w-lg space-y-2">
                  <p className="text-2xl font-bold text-emerald-600 mb-3">
                    {template.qualityScore}<span className="text-sm text-gray-400">/100</span>
                    <span className="text-xs text-gray-500 font-normal ml-3">premium feel {template.quality.premiumFeel}</span>
                  </p>
                  {Object.entries(template.quality.breakdown).map(([metric, value]) => (
                    <ScoreBar key={metric} label={metric} value={value} />
                  ))}
                </div>
              )}
              {activeTab === 'json' && <JsonBlock data={result} />}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAIPlayground;
